import * as React from 'react';
import { __ } from '../utilities';
import { css } from 'styled-components';
import { getProp } from '@sportnet/utilities';
import { rem } from 'polished';
import Button from '../Button';
import DomainSegment from './DomainSegment';
import DomainsApi from './DomainsApi';
import FormField from '../FormField';
import HorizontalLayout from '../Layouts/HorizontalLayout';
import Label from '../Label/Label';
import Message from '../Message';
import Text from '../Text';
import styled from 'styled-components';
const TestDomainInputWrapper = styled.div `
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;
const TestDomainPostfix = styled(Text) `
  margin-left: ${rem(8)};
`;
const MaxContentWidth = styled.div `
  max-width: ${rem(360)};
  flex-grow: 1;
`;
const Aliases = styled('div') `
  padding-top: ${rem(8)};
`;
const Alias = styled('div') `
  ${({ $error }) => !$error &&
    css `
      div {
        margin-bottom: ${rem(8)};
        padding-bottom: 0;
      }
    `}
`;
const Domains = ({ domain: domainProp, testDomainPostfix, accessToken, onChange, appId, appSpace, payload = {}, }) => {
    const [isFetching, setIsFetching] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [isRemoving, setIsRemoving] = React.useState(false);
    const [isTestDomain, setIsTestDomain] = React.useState(!!(testDomainPostfix &&
        (!(domainProp || '') || (domainProp || '').endsWith(testDomainPostfix))));
    const [domain, setDomain] = React.useState(domainProp || '');
    const [domainInfo, setDomainInfo] = React.useState(undefined);
    const [error, setError] = React.useState(undefined);
    const [errorTarget, setErrorTarget] = React.useState(undefined);
    const domainCheckInterval = React.useRef(undefined);
    React.useEffect(() => {
        DomainsApi.setToken(accessToken);
        if (domain) {
            (async () => {
                await getDomainInfo();
            })();
        }
        return () => {
            clearInterval(domainCheckInterval.current);
        };
    }, [accessToken, domain]);
    const registerDomainCheckInterval = () => {
        clearInterval(domainCheckInterval.current);
        domainCheckInterval.current = window.setInterval(async () => {
            const { status } = await DomainsApi.getDomainInfo(domain);
            if (status !== 'pending') {
                clearInterval(domainCheckInterval.current);
            }
            setDomainInfo(prevDomainInfo => {
                if (!prevDomainInfo)
                    return prevDomainInfo;
                return {
                    ...prevDomainInfo,
                    status,
                };
            });
        }, 5000);
    };
    const getDomainInfo = async () => {
        setIsFetching(true);
        try {
            const response = await DomainsApi.getDomainInfo(domain);
            if (response.status === 'pending') {
                registerDomainCheckInterval();
            }
            setDomainInfo(response);
            setError(null);
        }
        catch (e) {
            if (getProp(e, ['details', 'name']) === 'DOMAIN_NOT_FOUND') {
                setError(__('Pre danú doménu neexistuje žiadne nastavenie.'));
            }
            else {
                setError(__('Nastala neočakávaná chyba.'));
            }
        }
        finally {
            setIsFetching(false);
        }
    };
    const onChangeDomain = (e) => {
        const domain = e.currentTarget.value.trim();
        const newState = {
            domain,
        };
        if (domain.length >= 4 && !domain.startsWith('www.')) {
            newState.domainInfo = {
                aliases: [`www.${domain}`],
            };
        }
        setDomain(newState.domain);
        setDomainInfo(newState.domainInfo);
    };
    const onChangeAlias = (value, idx) => {
        setDomainInfo(prevState => {
            if (!prevState)
                return prevState;
            const aliases = [...(prevState ? prevState.aliases || [] : [])];
            aliases[idx] = value.trim();
            return {
                ...prevState,
                aliases,
            };
        });
    };
    const renderStatus = (status) => {
        switch (status) {
            case 'pending':
            case 'updated':
                return (React.createElement(Message, { warning: true, title: __('Prebieha konfigurácia') }, __('Aktuálne prebieha nastavovanie domény a aliasov.')));
            case 'live':
                return (React.createElement(Message, { success: true, title: __('Aktívna') }, __('Doména je úspešne nastavená.')));
            case 'error':
                return (React.createElement(Message, { danger: true, title: __('Chyba') }, !!domainInfo && domainInfo.error));
            default:
                return null;
        }
    };
    const commitUpdateDomain = async (domainInfo) => {
        const aliases = (domainInfo.aliases || [])
            .map(i => i.trim())
            .filter(i => i);
        try {
            const response = await DomainsApi.appUpdateDomain(domainInfo.appId, domainInfo.ppo, domainInfo._id, {}, {
                aliases,
                payload,
            });
            if (response.status === 'pending') {
                registerDomainCheckInterval();
            }
            onChange(response);
            setDomainInfo(response);
        }
        catch (e) {
            if (getProp(e, ['details', 'name']) === 'UNAUTHORIZED') {
                setError(__('Neplatný autorizačný token.'));
            }
            else if (getProp(e, ['details', 'name']) === 'INVALID_DNS') {
                const dnsChecks = getProp(e, ['details', 'payload', 'dnschecks'], {});
                const failedDnsCheck = Object.keys(dnsChecks).find(i => dnsChecks[i] === false);
                setError(__('Nebolo možné získať nastavenie DNS.'));
                setErrorTarget(failedDnsCheck || null);
            }
            else if (getProp(e, ['details', 'name']) === 'DOMAIN_OR_ALIAS_EXISTS') {
                setError(__('Doména alebo niektorý alias sa už používa.'));
            }
            else {
                setError(__('Nastala neočakávaná chyba.'));
            }
        }
    };
    const commitCreateDomain = async () => {
        let aliases = (domainInfo?.aliases || []).map(i => i.trim()).filter(i => i);
        if (isTestDomain) {
            aliases = [`www.${domain}`];
        }
        try {
            const response = await DomainsApi.appCreateDomain(appId, appSpace, {}, {
                _id: domain,
                aliases,
                payload,
                cert: {
                    type: 'letsencrypt',
                },
            });
            if (response.status === 'pending') {
                registerDomainCheckInterval();
            }
            onChange(response);
            setDomainInfo(response);
        }
        catch (e) {
            if (getProp(e, ['details', 'name']) === 'INVALID_DOMAIN') {
                setError(__('Neplatná doména, prosím, skontrolujte názov domény.'));
            }
            else if (getProp(e, ['details', 'name']) === 'INVALID_DNS') {
                const dnsChecks = getProp(e, ['details', 'payload', 'dnschecks'], {});
                const failedDnsCheck = Object.keys(dnsChecks).find(i => dnsChecks[i] === false);
                setError(__('Nebolo možné získať nastavenie DNS.'));
                setErrorTarget(failedDnsCheck || null);
            }
            else if (getProp(e, ['details', 'name']) === 'DOMAIN_OR_ALIAS_EXISTS') {
                setError(__('Doména sa už používa.'));
            }
            else {
                setError(__('Nastala neočakávaná chyba.'));
            }
        }
    };
    const submit = async () => {
        if (!domain) {
            setError(__('Pole je povinné'));
            return;
        }
        setIsSubmitting(true);
        setError(null);
        setErrorTarget(null);
        if (domainInfo && domainInfo._id) {
            await commitUpdateDomain(domainInfo);
        }
        else {
            await commitCreateDomain();
        }
        setIsSubmitting(false);
    };
    const remove = async () => {
        if (domainInfo &&
            domainInfo._id &&
            window.confirm(__('Skutočne si želáte odstrániť nastavenie domény?'))) {
            setIsRemoving(true);
            setError(null);
            setErrorTarget(null);
            try {
                await DomainsApi.appDeleteDomain(appId, appSpace, domainInfo._id);
                setDomainInfo(null);
                clearInterval(domainCheckInterval.current);
                onChange(null);
            }
            catch (e) {
                console.error(e);
                setError(__('Nastala neočakávaná chyba.'));
            }
            finally {
                setIsRemoving(false);
            }
        }
    };
    const toggleTestDomain = () => {
        setIsTestDomain(prevState => !prevState);
    };
    const onChangeTestSubDomain = (e) => {
        const subDomain = e.currentTarget.value.trim();
        const domain = `${subDomain}${testDomainPostfix}`;
        const newState = {
            domain,
        };
        if (domain.length >= 4 && !domain.startsWith('www.')) {
            newState.domainInfo = {
                aliases: [`www.${domain}`],
            };
        }
        setDomain(newState.domain);
        setDomainInfo(newState.domainInfo);
    };
    const showTestDomainSettings = testDomainPostfix && !(domain && !isTestDomain);
    const showCustomDomainSettings = !(domain && isTestDomain);
    const showSaveButton = !(isTestDomain && domainInfo && domainInfo._id);
    return (React.createElement(React.Fragment, null,
        !!domainInfo && renderStatus(domainInfo.status),
        showTestDomainSettings && (React.createElement(DomainSegment, { active: isTestDomain, title: __('Testovacia doména'), onClick: toggleTestDomain },
            React.createElement(TestDomainInputWrapper, null,
                React.createElement(MaxContentWidth, null,
                    React.createElement(FormField, { error: error && (!errorTarget || errorTarget === domain)
                            ? error
                            : void 0, label: __('Doména'), placeholder: __('Zadajte názov subdomény'), value: testDomainPostfix && domain
                            ? domain.split(testDomainPostfix)[0]
                            : '', loading: isFetching, onChange: onChangeTestSubDomain })),
                React.createElement(TestDomainPostfix, null, testDomainPostfix)))),
        showCustomDomainSettings && (React.createElement(DomainSegment, { active: !isTestDomain, title: __('Vlastná doména'), onClick: toggleTestDomain },
            React.createElement(MaxContentWidth, null,
                React.createElement(FormField, { error: error && (!errorTarget || errorTarget === domain)
                        ? error
                        : void 0, label: __('Doména'), placeholder: __('Zadajte názov domény'), disabled: (!!domainInfo && !!domainInfo._id) || isFetching, value: domain, loading: isFetching, onChange: onChangeDomain }),
                React.createElement(Label, null, __('Aliasy')),
                React.createElement(Aliases, null, [...(domainInfo ? domainInfo.aliases || [] : []), ''].map((alias, idx) => (React.createElement(Alias, { key: idx, "$error": errorTarget === alias },
                    React.createElement(FormField, { error: errorTarget === alias && error ? error : void 0, value: alias, onChange: (e) => onChangeAlias(e.currentTarget.value, idx) })))))))),
        !isFetching && (React.createElement(HorizontalLayout, { wrap: true },
            showSaveButton && (React.createElement(Button, { loading: isSubmitting, primary: true, onClick: submit }, __('Uložiť zmeny'))),
            !!domain && !error && (React.createElement(Button, { loading: isRemoving, basic: true, onClick: remove }, __('Odpojiť doménu')))))));
};
export default Domains;
